/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { unwrapReactive } from '@jack-henry/frontend-utils/lit';
import { ConfigurationService } from '@treasury/core/config';
import { NavigationService } from '@treasury/core/navigation';
import { MessagesService } from '@treasury/domain/channel/services';
import type { Message } from '@treasury/domain/channel/services/messages';
import { MessageCenterService, MessageType } from '@treasury/domain/message-center';
import { Feature } from '@treasury/domain/services/feature-flags';
import { TmContainer } from '@treasury/presentation';
import { refreshIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-action-button';
import '@treasury/presentation/components/tm-blocking-loader';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-icon';
import '@treasury/presentation/components/tm-list-header';
import type { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-tabs';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import '../../dashboard/components/cutoff-times-header-item';
import '../../dashboard/components/notifications-header-item';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import { MenuItem } from '../../services/types/jhd-header-bar.type';
import '../partials/message-center-filter-sheet';
import '../partials/message-detail';
import '../partials/messages-table';

export const tagName = 'message-center-container';
@customElement(tagName)
export class MessageCenterContainer extends TmContainer {
    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private readonly service: MessagesService;

    @InjectProperty()
    declare private messageCenterService: MessageCenterService;

    @InjectProperty()
    declare private config: ConfigurationService;

    @state()
    private showCutoffTooltip = false;

    @state()
    private showNotificationsTooltip = false;

    @state()
    private creatingMessage = false;

    @state()
    private loadingMessageDetails = false;

    @property({ type: Array })
    private menuItems = this.createMenuItems();

    @state()
    private tabs = [
        { label: `Inbox`, id: MessageType.Received },
        {
            label: `Sent`,
            id: MessageType.Sent,
        },
        {
            label: `Archived`,
            id: MessageType.Archived,
        },
    ];

    @state()
    private activeTab = MessageType.Received;

    @state()
    private messages: Message[] = [];

    @state()
    private filteredMessages: Message[] = [];

    @state()
    private messageDetailSheetIsOpen = false;

    @state()
    private selectedMessage: Message = {} as Message;

    @state()
    private messageDetails: Message[] = [];

    @state()
    private downloading = false;

    @state()
    private filterString = '';

    @state()
    private hasAdvancedFilters = false;

    @state()
    private filtersOpen = false;

    private get currentTabId() {
        return this.activeTab;
    }

    private get filteredMessagesLength() {
        return this.filteredMessages.length;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.showNotificationsTooltip = screen.width > 1100;
        this.headerService.configure({ title: 'Message Center', menuItems: this.menuItems });
        this.switchMessageType(this.activeTab);
    }

    protected async firstUpdated() {
        const ffService = await this.featureFlagService;
        this.hasAdvancedFilters = await ffService.isEnabled(
            Feature.PwaMessageCenterAdvancedFilters
        );
    }

    protected render() {
        return html`
            <message-center-filter-sheet
                .open=${this.filtersOpen}
                @close=${() => {
                    this.filtersOpen = false;
                }}
                @apply-filters=${(e: CustomEvent) => {
                    const advancedFilters = e.detail;
                    this.filtersOpen = false;
                    this.loading = true;
                    this.performAdvancedSearch(advancedFilters);
                }}
            ></message-center-filter-sheet>
            ${this.renderBlockingLoader()}
            <tm-action-button
                class="mt-[8px] mb-[8px]"
                .buttonTitle=${'New Message'}
                @click=${() => this.openComposeMessageSheet()}
            ></tm-action-button>
             <tm-section
                    class=${classMap({
                        'flex flex-col': true,
                        'overflow-hidden h-full': !this.isDesktop,
                        'mx-2 mb-12 pt-2 rounded-lg h-[92%] pb-[25px]': this.isDesktop,
                    })}
                >
            <div>
                <div class="flex justify-between px-4 pt-4">
                    <span class="text-lg font-medium">Messages</span>
                    <button class="refresh" @click=${() => this.switchMessageType(this.activeTab)}>
                        <tm-icon
                            .icon=${refreshIcon}
                            .color=${'var(--jh-color-gray-600)'}
                        ></tm-icon>
                    </button>
                </div>
               
                    <tm-tabs
                        .tabs=${this.tabs}
                        .activeTab=${this.activeTab}
                        @switchTab=${(event: CustomEvent) => {
                            if (event.detail.activeTab !== MessageType.AdvancedSearch) {
                                this.activeTab = Number(event.detail.activeTab);
                                this.switchMessageType(this.activeTab);
                                if (this.tabs.length === 4) {
                                    this.tabs = this.tabs.filter(
                                        tab => tab.id !== MessageType.AdvancedSearch
                                    );
                                }
                            }
                        }}
                    ></tm-tabs>
                    ${this.renderTableHeader()} ${this.renderMessagesTable()}
                </tm-section>
            </div>
            <tm-bottom-sheet
                .open=${this.messageDetailSheetIsOpen}
                @close=${() => {
                    this.messageDetailSheetIsOpen = false;
                }}
            >
                <div
                    slot="header-center"
                    class="flex flex-col items-center font-medium leading-tight"
                >
                    ${this.selectedMessage?.subject}
                    <div class="font-normal text-xs">${this.selectedMessage?.messageId}</div>
                </div>

                <message-detail
                    .messages=${this.messageDetails}
                    @archive-message=${() => this.archiveMessage()}
                    @download-attachment=${({ detail }: CustomEvent) =>
                        this.downloadAttachment(detail)}
                    .loading=${this.loading}
                    .downloading=${this.downloading}
                    @send-message=${(e: CustomEvent) => this.sendMessage(e)}
                ></message-detail>
            </tm-bottom-sheet>
        `;
    }

    private createMenuItems(): MenuItem[] {
        return [
            {
                title: 'Notifications',
                icon: html`<notifications-header-item
                    .institution=${this.config.institutionId}
                    .showNotificationsTooltip=${this.deviceService.isDesktop}
                ></notifications-header-item>`,
                action: () => {
                    if (!this.showNotificationsTooltip) {
                        this.navService.navigate(`/notifications`);
                    }
                },
            },
            {
                title: 'Cutoff Times',
                icon: html`<cutoff-times-header-item
                    .showCutoffTooltip=${this.showCutoffTooltip}
                ></cutoff-times-header-item>`,
                action: () => {
                    this.showCutoffTooltip = true;
                },
            },
        ];
    }

    private openComposeMessageSheet() {
        this.navService.navigate('/message-center/compose');
    }

    private async messageSelected(message: Message) {
        this.selectedMessage = message;
        this.loadingMessageDetails = true;
        this.messageDetails = (await this.service.getMessage(message.id)) as unknown as Message[];
        this.messageDetailSheetIsOpen = true;
        this.loadingMessageDetails = false;
        if (this.activeTab !== MessageType.AdvancedSearch) {
            this.switchMessageType(this.activeTab);
        }
    }

    private async archiveMessage() {
        this.loading = true;
        await this.service.archiveMessage(this.selectedMessage);
        this.messages = (await this.service.fetchMessages(this.activeTab)) as unknown as Message[];
        this.loading = false;
        this.messageDetailSheetIsOpen = false;
        this.switchMessageType(this.activeTab);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private async downloadAttachment(detail: any) {
        this.downloading = true;
        await this.service.downloadAttachment(detail.attachmentId, detail.attachmentName);
        this.downloading = false;
    }

    private async sendMessage(e: CustomEvent) {
        const message = unwrapReactive(e.detail);
        this.creatingMessage = true;
        await this.service.sendMessage(message);
        this.messages = (await this.service.fetchMessages(this.activeTab)) as unknown as Message[];
        this.creatingMessage = false;
        this.messageDetailSheetIsOpen = false;
        this.navService.navigate(`/message-center`);
    }

    private async switchMessageType(messageType: number) {
        this.loading = true;
        this.filterString = '';
        this.messages = (await this.service.fetchMessages(messageType)) as unknown as Message[];
        this.filteredMessages = this.messages;
        this.loading = false;
    }

    private filterResults(e: CustomEvent) {
        const { value } = e.detail;
        this.filterString = value;
        this.filteredMessages = this.messages.filter(
            (message: Message) =>
                message.subject.toLowerCase().includes(value.toLowerCase()) ||
                message.messageId.includes(value) ||
                message.sentDate.includes(value)
        );
    }

    private async performAdvancedSearch(advancedFilters: Record<string, unknown>) {
        this.loading = true;
        try {
            this.messages = (await this.messageCenterService.getMessageCenterMessages(
                advancedFilters
            )) as unknown as Message[];
            this.filteredMessages = this.messages;
            if (this.tabs.length === 3) {
                this.tabs = [
                    ...this.tabs,
                    { label: 'Advanced Search', id: MessageType.AdvancedSearch },
                ];
            }
            this.activeTab = 5;
        } catch (error) {
            this.notificationService.renderError(
                (error as Error) || 'Failed to perform advanced messages search'
            );
        } finally {
            this.loading = false;
        }
    }

    private renderMessagesTable() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        return html`<messages-table
            .messages=${this.filteredMessages}
            @message-selected=${(event: CustomEvent) => {
                this.messageSelected(event.detail);
            }}
        ></messages-table>`;
    }

    private renderBlockingLoader() {
        if (!this.creatingMessage && !this.loadingMessageDetails) {
            return nothing;
        }
        return html`<tm-blocking-loader></tm-blocking-loader>`;
    }

    private renderTableHeader() {
        const tabLabel = this.tabs.find(tab => tab.id === this.activeTab)?.label;
        return html` <tm-list-header
            .placeholder=${tabLabel ? `Search ${tabLabel}` : 'Search'}
            .filterString=${this.filterString}
            .filterButton=${this.hasAdvancedFilters}
            @filterButtonClick=${() => {
                this.filtersOpen = true;
            }}
            @search-term-change=${(e: TmSearchTermChangeEvent) => this.filterResults(e)}
        ></tm-list-header>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                }
                .header-icon path {
                    fill: var(--content-light);
                }
                h4 {
                    color: var(--header-text-color);
                }
                .card-content {
                    width: 100%;
                }
                .tabs {
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                }
                .tabs > ul {
                    display: flex;
                    align-items: center;
                }
                .tabs > ul > li {
                    margin-right: 10px;
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    cursor: pointer;
                }
                path {
                    fill: var(--omega-primary);
                }
                .detail-action > path {
                    fill: var(--content-light);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: MessageCenterContainer;
    }
}
